import { useAuthContext, usePermissions } from '/src/hooks';
import { WebP } from '/src/components/WebP';

import { UpsellWall } from '/src/components/UpsellWall';
import { FEATURES } from '/src/features/_global/constants';

import FeaturedImagePNG from '@crazyegginc/hatch/dist/images/illustration-upsell-ab-testing.png';
import FeaturedImageWebP from '@crazyegginc/hatch/dist/images/illustration-upsell-ab-testing.webp';
import LeftPeoplePhotoPNG from '@crazyegginc/hatch/dist/images/people-mike-loveridge.png';
import RightLogoWebP from '@crazyegginc/hatch/dist/images/logo-g2crowd-high-performer.webp';
import RightLogoPNG from '@crazyegginc/hatch/dist/images/logo-g2crowd-high-performer.png';
import { ReactComponent as LeftLogoIcon } from '@crazyegginc/hatch/dist/images/logo-intuit.svg';

const TEXT_LIST = [
  {
    text: 'Test headlines, CTAs, images, and forms to boost conversion rates',
  },
  {
    text: 'Sharpen your messaging to ensure it resonates and drives action',
  },
  {
    text: 'Lower customer acquisition costs by scaling A/B testing',
  },
];

const TextContent = ({ canManageBilling, ownerEmail, isStripeUser }) => {
  return (
    <>
      <UpsellWall.Title
        titleText={canManageBilling ? 'Turn every click into a conversion' : 'Request an upgrade to the Starter plan'}
        subtitleText={!canManageBilling ? 'Learn faster, test smarter, and convert more with the A/B testing.' : null}
      />
      {canManageBilling ? (
        <UpsellWall.List list={TEXT_LIST} />
      ) : (
        <>
          <p className="mb-6 text-black-pearl-500 text-sm">
            Contact your Account Admin at <strong>{ownerEmail}</strong> to upgrade and start launching A/B Tests.
          </p>
        </>
      )}
      <UpsellWall.CTA
        metricType="ab_testing_upsell_paywall"
        canManageBilling={canManageBilling}
        ownerEmail={ownerEmail}
        isStripeUser={isStripeUser}
      />
    </>
  );
};

const LeftContent = () => {
  return (
    <>
      <UpsellWall.TestimonialText textClassNames="mb-10">
        We recently scaled to 20 tests a month. It&apos;s amazing how different everything is at the company when you
        have that additional customer data to work with.
      </UpsellWall.TestimonialText>
      <UpsellWall.TestimonialPerson
        name="Mike Loveridge"
        title="Senior Marketing Manager"
        imagePNG={LeftPeoplePhotoPNG}
        imageWidth="40"
        imageHeight="40"
        logoSVG={LeftLogoIcon}
        logoWidth="137"
        logoHeight="40"
        logoAlt="Intuit"
      />
    </>
  );
};

const RightContent = () => {
  return (
    <div className="flex">
      <WebP
        webp={RightLogoWebP}
        fallback={RightLogoPNG}
        width="80"
        height="84"
        alt="high performer winter 2024"
        className="mr-5"
      />
      <div className="flex-1">
        <UpsellWall.TestimonialText textClassNames="mb-6">
          Crazy Egg provides valuable insight into the performance of our site. The A/B testing tool has helped us see
          which web page variants work best for our audience and helped us determine what changes to make for a better
          user experience.
        </UpsellWall.TestimonialText>
        <UpsellWall.TestimonialPerson
          name="Validated G2 Reviewer"
          title="Digital Content Strategist, Small-Business (50 or fewer employees)"
        />
      </div>
    </div>
  );
};

export function ABTestingUpsellPaywall() {
  const { currentAccount } = useAuthContext();

  const permissions = usePermissions();
  const canManageBilling = permissions.can('edit', FEATURES.BILLING).allowed;
  const isStripeUser = Boolean(currentAccount?.useStripe);

  return (
    <UpsellWall>
      <UpsellWall.TopPanel
        textContent={
          <TextContent
            canManageBilling={canManageBilling}
            ownerEmail={currentAccount.ownerEmail}
            isStripeUser={isStripeUser}
          />
        }
        imagePNG={FeaturedImagePNG}
        imageWebP={FeaturedImageWebP}
        imageWidth="522"
        imageHeight="264"
        imageAlt="ab testing feature"
      />
      <UpsellWall.BottomPanel isSeparate={true} leftContent={<LeftContent />} rightContent={<RightContent />} />
    </UpsellWall>
  );
}
